import { CClient } from '@common/utils'
import BuiltInIndia from '@components/atoms/built-in-india'
import NavBar from '@components/book-trial-class/navbar'
import EventCard from '@components/competitions/event-card'
import { CompetitionRegistrationContainer } from '@containers/competitions/competition-registration-container'
import { CompetitionSubmissionContainer } from '@containers/competitions/competition-submission-container'
import { COMPETITIONS_PHASES } from '@lib/enums/competitions'
import { redirectToForGetInitialProps } from '@lib/utils/common'
import date, { currentYear } from '@lib/utils/date.utils'
import { NextSeo } from 'next-seo'
import { parseCookies } from 'nookies'
import { threeMemberTeamCompetitions } from '@lib/constants/competitions'

type Props = {
  data: any
  phone: string
  nextRedirect?: string
  isRegistrationAllowed: boolean
  isSubmissionAllowed: boolean
}

export const CompetitonPhasePage = ({
  data,
  phone,
  nextRedirect,
  isRegistrationAllowed,
  isSubmissionAllowed,
}: Props): JSX.Element => {
  const { registrationForm, start, end, title, organizer, phases, slug } = data
  const { linkedEntity } = phases[0]
  const { id, submissionForm } = linkedEntity
  return (
    <>
      <NextSeo
        title={data.metaTitle}
        description={data.metaDescription}
        openGraph={{
          title: data.metaTitle,
          description: data.metaDescription,
          type: 'website',
          url: `https://www.codingal.com/competitions/${data.slug}/register/`,
          // eslint-disable-next-line @typescript-eslint/camelcase
          site_name: 'Codingal',
          images: [
            {
              url: data.ogImage,
              alt: `Codingal ${data.title}`,
            },
          ],
        }}
      />
      <div className="flex flex-col h-full min-h-screen">
        <NavBar />
        <div className="flex overflow-hidden flex-1 justify-between items-start h-full">
          <div className="container mx-auto h-full">
            <div className="flex flex-wrap justify-center my-4">
              <div className="hidden mx-4 mt-10 w-full h-full lg:flex lg:w-1/2 lg:flex-col">
                <EventCard
                  title={title}
                  start={date(start).format('MMM D, YYYY hh:mm A')}
                  end={date(end).format('MMM D, YYYY hh:mm A')}
                  teamSize={
                    threeMemberTeamCompetitions.includes(slug)
                      ? 'Upto 3 members'
                      : '1 member'
                  }
                  orgName={organizer.title}
                  orgWebsite={organizer.website}
                  orgLogo={organizer.logo}
                  data={data}
                />
              </div>
              {isRegistrationAllowed && (
                <CompetitionRegistrationContainer
                  formData={registrationForm}
                  slug={data.slug}
                  phone={phone}
                  nextRedirect={nextRedirect}
                  title={title}
                  start={start}
                  end={end}
                />
              )}

              {isSubmissionAllowed && (
                <CompetitionSubmissionContainer
                  formData={submissionForm}
                  slug={data.slug}
                  linkedEntityId={id}
                />
              )}
            </div>
            <div className="my-6 text-center">
              <span>
                ©️ {currentYear} Codingal. <BuiltInIndia />
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

CompetitonPhasePage.getInitialProps = async (context) => {
  const { query } = context
  const { competitionName, phase } = query
  const token = parseCookies(context).token
  const { REGISTER, SUBMIT } = COMPETITIONS_PHASES

  if (phase !== REGISTER && phase !== SUBMIT) {
    redirectToForGetInitialProps(context, '/404/')
    return {}
  }

  if (phase === SUBMIT && !token) {
    return {}
  }

  const response = await CClient(`/api/v1/competitions/${competitionName}/`, {
    method: 'GET',
    headers: {
      Authorization: token ? `Token ${token}` : null,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    redirectToForGetInitialProps(context, `/${response.status}`)
    return {}
  }

  if (response.ok) {
    const results = await response.json()
    const { start, end, slug, isRegistered } = results
    const isUpcoming = date(start).isAfter(date())
    const isOngoing = date().isBetween(start, end)
    const ended = date(end).isBefore(date())
    const isRegistrationAllowed =
      (isUpcoming || isOngoing) && !ended && !isRegistered && phase === REGISTER
    const isSubmissionAllowed =
      isOngoing && isRegistered && !ended && phase === SUBMIT
    if (!isRegistrationAllowed && !isSubmissionAllowed) {
      redirectToForGetInitialProps(context, `/competitions/${slug}`)
      return {}
    }
    return {
      data: results,
      phone: query.phone || null,
      nextRedirect: query.next || null,
      isRegistrationAllowed,
      isSubmissionAllowed,
    }
  }
}

export default CompetitonPhasePage
